'use strict';
/* ==========================================================================
   levels/video.js — step 1, JUST A THEORY?: the interactive video (shared/engine/v2/core/player.js), Energy's
   engraving collage on BLUEPRINT paper (DESIGN.md §2), one scene per script segment (content/script.json s1–s5; each
   segment's "visual" is the storyboard). Model: lessons/blue-eyes/levels/video.js (chapter cards, captions in the top
   band, the numbered working on EVERY branch, branch cues, the portrait dock notes).

   Every scene is a PURE FUNCTION of the part time t: beats are keyed to the dialogue through ctx.L / ctx.E / ctx.W
   (via cues(), which turns the ±Infinity of other parts into ±1e6), so the choreography follows whatever the voice
   timing is (the text timing now, the recorded clips later, with no code change) and the player can seek, replay and
   run at 25×. Branches: a reply cue is min() over the question's reply ids (branch()): in the reply part only the
   branch that plays is finite, in the lines part all are +∞ (not yet), in the after part all are −∞ (done). A visual
   that belongs to ONE branch is gated on the answer (ansOf()).

   Honesty (DESIGN.md §4): every planet point, ratio, prediction and the q3 working come from core-local/nos.js
   (Nos.PLANETS / years / ratio / q3 / fmt) and the shared bank evaluator (Bank.fill / eval / format on the script's
   `working`, cross-checked against Nos.q3: nums().checks); card texts about the laws and theories come from Nos.ITEMS /
   Nos.TERMS (`kid`). The only literals are the storyboard's own words: Newton's sentence (Motte 1729, facts 3.3), the
   years it names (Kepler 1619, general relativity 1915, drift 1912, the 1960s: facts 3.4, 3.2, 3.12, Correction 10),
   its sticky notes and its captions for the rocket, the atom and NASA's label (facts 2.10, 3.7, 3.9).
   Never "proven", never "educated guess", never a ≈ / → / ✓ glyph ("about 1"; ticks and arrows are drawn in ink).

   Staging: each segment opens on a chapter card (blueprint + cream index card) that tears away; Max (left, arms
   crossed / pointing) and Dr. Voss (right, clipboard / pointing) stand full size, captions in the top band on the
   speaker's side; the boards use the middle (x 440–1480, y 230–930). Portrait: the player puts the captions in the
   dock; each scene writes its board text into the dock too (Dock.text).
   Art (each optional, engraving code stand-ins until the assets agent lands them): IMG.science_fair_board (via
   Fair.board), telescope, planet, rock_layers, flower_pot. QA: tools/qa_video.js; debug: window.TheoryVideo.
   ========================================================================== */
(function () {
  const S = Player.scenes;
  const HANDF = "'Caveat Brush'", TYPE = "'Special Elite'", SLAB = "'Alfa Slab One'", MARK = "'Luckiest Guy'";
  const F = () => window.Fair;
  const FC = () => window.Fair.C;
  const INK = '#2b2420', PENCIL = 'rgba(43,36,32,0.62)', RED = '#c8321f', NAVY = '#1f3552', PINK = '#f4c6c0', STICKY = '#f2d479';
  // the storyboard's own words (content/script.json visuals)
  const NEWTON = { quote: 'I have not been able to discover the cause of those properties of gravity from phenomena, and I frame no hypotheses.', by: 'Isaac Newton (1713; translated 1729)' };   // facts 3.3, Correction 8
  const GRAV_CARD = 'how hard two masses pull on each other (a math rule)';   // the storyboard's card (s2 visual; facts 3.1)
  const YEARS = { kepler: 1619, relativity: 1915, drift: 1912, seafloor: '1960s' };   // facts 3.4, 3.2, 3.12, Correction 10

  /* ---------------- small helpers ---------------- */
  const fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  const cues = ctx => ({ L: id => fin(ctx.L(id)), E: id => fin(ctx.E(id)), W: (id, w, n = 1) => fin(ctx.W(id, w, n)) });
  const segs = () => Game.content.script.segments;
  const segIndex = seg => segs().indexOf(seg);
  const segById = id => segs().find(s => s.id === id);
  const fx = (name, alt) => (Audio.has(name) ? name : alt);
  const ansOf = (ctx, part) => { const q = part.seg.question; if (!q) return null; return ctx.answer != null ? ctx.answer : part.answer != null ? part.answer : q.correct; };
  const docked = () => typeof UI !== 'undefined' && UI.docked && UI.docked();
  function fontOk() { return typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded'; }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  const WRAPS = new Map();
  function wrapLines(str, maxW, size, font = HANDF) {
    const key = str + '|' + maxW + '|' + size + '|' + font;
    if (WRAPS.has(key)) return WRAPS.get(key);
    const words = String(str).split(/\s+/), out = []; let cur = '';
    for (const w of words) { const nx = cur ? cur + ' ' + w : w; if (cur && tw(nx, `${size}px ${font}`) > maxW) { out.push(cur); cur = w; } else cur = nx; }
    if (cur) out.push(cur);
    if (fontOk()) { if (WRAPS.size > 500) WRAPS.clear(); WRAPS.set(key, out); }
    return out;
  }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  function txt(str, x, y, size, o = {}) {
    g.save(); g.font = `${size}px ${o.font || HANDF}`; g.fillStyle = o.color || INK; g.textAlign = o.align || 'center'; g.textBaseline = 'middle';
    if (o.alpha != null) g.globalAlpha *= o.alpha;
    if (o.rot) { g.translate(x, y); g.rotate(o.rot); g.fillText(str, 0, 0); } else g.fillText(str, x, y);
    g.restore();
  }
  /* the min over a question's reply ids of a cue word per branch (tbl: {replyId: word | [word, n] | null = its start}) */
  function branch(c, tbl) {
    let best = Infinity;
    for (const id in tbl) { const v = tbl[id]; if (v === undefined) continue; if (v === null) { best = Math.min(best, c.L(id)); continue; } const [w, n] = Array.isArray(v) ? v : [v, 1]; best = Math.min(best, c.W(id, w, n)); }
    return best;
  }
  const replyIds = q => q.right.concat(...Object.values(q.wrong || {}));
  const replyStart = (c, q) => Math.min(...replyIds(q).map(id => c.L(id)));
  const gone = (t, t0, d = 0.4) => 1 - E.inCubic(P(t, t0, t0 + d));      // 1 → 0 (a piece leaving)

  /* ======================================================================
     Layers: static art baked once into an off-screen canvas (the engine's OFF budget evicts them like sprites).
     layer(key, x, y, w, h, draw, {zoom}) — draw() paints the rect (x, y, w, h) in the caller's coordinates.
     ====================================================================== */
  const LY = new Map();
  function layer(key, x, y, w, h, draw, o = {}) {
    const res = +Math.min(3, Math.max(0.3, DS * (o.zoom || 1))).toFixed(3);
    let e = LY.get(key);
    if (!e || e.dead || e.res !== res) {
      if (e && !e.dead) OFF.drop(e);
      LY.delete(key);
      const pw = Math.ceil(w * res), ph = Math.ceil(h * res);
      if (!FX.sprites || typeof _bake !== 'function' || !fontOk() || !OFF.room(pw * ph * 4)) { draw(); return false; }
      const cv = _bake(pw, ph, cx => cx.setTransform(res, 0, 0, res, -x * res, -y * res), draw);
      e = OFF.add(cv, { res, x, y, w, h });
      const me = e; e.drop = () => { if (LY.get(key) === me) LY.delete(key); };
      LY.set(key, e);
    }
    e.frame = OFF.frame;
    g.drawImage(e.c, x, y, e.c.width / res, e.c.height / res);
    return true;
  }
  const artKey = (...names) => names.map(n => (IMG[n] ? 1 : 0)).join('');

  /* the gym on blueprint paper: the wall + the floor (baked) */
  function bg(floorY = 930) {
    layer('bg|' + floorY + '|' + artKey('tex_blueprint'), 0, 0, 1920, 1080, () => { F().backdrop(); F().floor(floorY); }, { zoom: 0.75 });
  }

  /* ---------------- paper bits ---------------- */
  function strike(x0, y0, x1, y1, prog, seed = 1, w = 9) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color: RED, seed, prog, passes: 2, step: 10 }); }
  function tick(x, y, s, prog, seed = 1, color = '#3f8f3a') { ink([[x - 30 * s, y - 2 * s], [x - 8 * s, y + 22 * s], [x + 34 * s, y - 32 * s]], { w: 9 * s, color, seed, prog, passes: 2, step: 8 }); }
  /* a paper label: str on a paper rect (x, y = centre), static text */
  function tag(str, x, y, size, s = 1, o = {}) {
    if (s <= 0.001) return;
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    const font = `${size}px ${o.font || HANDF}`, w = Math.round(tw(str, font) + size * 0.9), h = Math.round(size * 1.4);
    paperRect(0, 0, w, h, o.color || FC().cream, { seed: o.seed || 41, depth: o.depth ?? 1.0, outline: true });
    g.font = font; g.fillStyle = o.ink || INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, size * 0.05);
    g.restore();
  }
  /* a sticky note with hand-written lines (lineT: one write-on time per line); strike: a red line across the note, or
     strikeLines: [{i, t}] strike single lines */
  function sticky(lines, x, y, t0, o = {}) {
    const s = pop(T, t0, 0.35); if (s <= 0) return null;
    const w = o.w || 300, size = o.size || 36, seed = o.seed || 610, h = Math.round(44 + lines.length * size * 1.15);
    const ly = i => -h / 2 + 22 + size * 0.62 + i * size * 1.15;
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    paperRect(0, 0, w, h, o.color || STICKY, { seed, depth: 1.2 });
    lines.forEach((l, i) => hand(l, 0, ly(i), size, o.lineT ? o.lineT[i] : t0 + 0.1 + i * 0.12, { color: o.ink || INK, seed: seed + i + 1, dur: 0.35, font: o.font || HANDF }));
    if (o.strike != null && T >= o.strike) strike(-w / 2 + 16, h / 2 - 18, w / 2 - 16, -h / 2 + 18, P(T, o.strike, o.strike + 0.35), seed + 9);
    (o.strikeLines || []).forEach(({ i, t: st }, k) => {
      if (T < st) return;
      const lw = Math.min(w - 20, tw(lines[i], `${size}px ${o.font || HANDF}`) + 16);
      strike(-lw / 2, ly(i) + 3, lw / 2, ly(i) - 3, P(T, st, st + 0.3), seed + 30 + k, 7);
    });
    tape(0, -h / 2 + 2, 96, 0.05, undefined, seed + 20);
    g.restore();
    return { w, h, ly };
  }
  /* a rubber stamp (no knock-out specks: they would punch through the whole frame) */
  function stampMark(text, x, y, s, o = {}) {
    if (s <= 0.001) return;
    const size = o.size || 72, color = o.color || RED, font = `${size}px ${TYPE}`;
    const w = Math.round(tw(text, font) + size * 0.9), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.12); g.scale(s, s); g.globalAlpha *= o.alpha ?? 0.9;
    g.strokeStyle = color; g.fillStyle = color; g.lineJoin = 'round';
    g.lineWidth = size * 0.1; pathPts(rectPts(w, h, 40 + text.length, true, 4)); g.stroke();
    g.lineWidth = size * 0.035; pathPts(rectPts(w - Math.round(size * 0.26), h - Math.round(size * 0.26), 41 + text.length, true, 3)); g.stroke();
    g.font = font; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, size * 0.07);
    g.restore();
  }
  const stampS = (t, t0) => (t < t0 ? 0 : E.outBack(P(t, t0, t0 + 0.25), 1.6));
  /* a law / theory / hypothesis / plain card whose body is WRITTEN ON (Fair.card draws the paper + title + stamp) */
  function wcard(kind, x, y, w, h, o = {}) {
    const s = o.s ?? pop(T, o.t0 ?? -1e6, 0.35, 1.8);
    if (s <= 0.001) return null;
    const K = F().card(kind, x, y, w, h, { title: o.title || '', body: '', stamp: o.stamp ?? false, seed: o.seed || 1, rot: o.rot || 0, s, hi: o.hi || null, flip: o.flip ?? 1 });
    if (o.body && (o.flip ?? 1) > 0.98) {
      const size = o.size || 34, lines = wrapLines(o.body, w - 48, size), inkC = kind === 'law' ? FC().lawInk : kind === 'theory' ? FC().amberInk : kind === 'hypothesis' ? FC().hypoInk : INK;
      g.save(); g.translate(x, y); g.rotate(o.rot || 0); g.scale(s, s);
      lines.forEach((l, i) => hand(l, -w / 2 + 24, Math.round(-h / 2 + 62 + size * 0.6 + i * size * 1.08), size, (o.bodyT ?? -1e6) + i * 0.35, { align: 'left', color: inkC, seed: (o.seed || 1) + 60 + i, dur: o.bodyT != null ? Math.min(0.9, 0.2 + l.length * 0.03) : 0.01 }));
      g.restore();
    }
    return K;
  }
  /* a card that flips (u 0..1) from one face to the other: {kind, title, body, stamp} */
  function flipCard(front, back, x, y, w, h, u, o = {}) {
    const face = u < 0.5 ? front : back, f = Math.max(0.03, Math.abs(Math.cos(Math.PI * clamp(u))));
    return wcard(face.kind, x, y, w, h, { ...o, title: face.title, body: face.body, stamp: face.stamp, flip: f, hi: u >= 1 ? o.hi : null, size: o.size });
  }

  /* ======================================================================
     Props: the engraving art when it lands (assets/img/<name>.png), else engraving-style code stand-ins, baked.
     prop(name, x, y, h, o): x = centre, y = the BASE (bottom), h = height; o = {s, rot, flip}
     ====================================================================== */
  const hatch = (x0, y0, x1, y1, n, seed, color = 'rgba(43,36,32,0.35)', w = 2) => { for (let i = 0; i < n; i++) { const u = (i + 0.5) / n; ink([[lerp(x0, x1, u) - 6, y0], [lerp(x0, x1, u) + 6, y1]], { w, color, seed: seed + i, passes: 1, step: 30, amp: 0.6 }); } };
  const shape = (k, pts) => Object.assign(pts, { k: 'theory:' + k });
  const SH = {
    tube: shape('tube', [[-22, -150], [22, -150], [15, 110], [-15, 110]]),
    pot: shape('pot', [[-60, -120], [60, -120], [44, 0], [-44, 0]]),
    wrench: shape('wrench', [[-11, -70], [11, -70], [13, 70], [-13, 70]]),
    can: shape('can', [[-50, -44], [50, -44], [56, 44], [-56, 44]]),
    rocket: shape('rocket', [[0, -80], [16, -52], [18, 40], [-18, 40], [-16, -52]]),
    fin: shape('fin', [[0, 0], [22, 30], [0, 22]]),
    cap: shape('cap', [[0, -26], [70, 0], [0, 26], [-70, 0]]),
    sa: shape('sa', [[0, -130], [38, -118], [74, -88], [86, -46], [66, -4], [46, 40], [24, 92], [2, 136], [-14, 112], [-20, 64], [-38, 22], [-62, -28], [-54, -82], [-30, -114]]),
    af: shape('af', [[-6, -118], [44, -128], [88, -106], [112, -66], [96, -22], [84, 30], [56, 82], [30, 118], [8, 104], [-2, 62], [-24, 22], [-66, 2], [-88, -30], [-66, -84], [-34, -108]]),
    patch: shape('patch', [[-120, -40], [120, -44], [124, 40], [-118, 44]]),
  };
  const STAND = {
    telescope: (h) => {           // a brass-banded tube on a tripod
      const k = h / 300;
      g.save(); g.scale(k, k);
      ink([[0, -110], [-70, 0]], { w: 6, color: '#5a3b1c', seed: 701, passes: 2, step: 20 });
      ink([[0, -110], [70, 0]], { w: 6, color: '#5a3b1c', seed: 702, passes: 2, step: 20 });
      ink([[0, -110], [8, 0]], { w: 6, color: '#5a3b1c', seed: 703, passes: 2, step: 20 });
      g.save(); g.translate(0, -170); g.rotate(-0.95);
      paperPiece(SH.tube, 0, 0, '#2f4a66', { seed: 704, depth: 1 });
      paperRect(0, -140, 52, 16, '#c9a24a', { seed: 705, depth: 0.3, boil: false });
      paperRect(0, -20, 44, 12, '#c9a24a', { seed: 706, depth: 0.3, boil: false });
      paperRect(0, 100, 36, 12, '#c9a24a', { seed: 707, depth: 0.3, boil: false });
      hatch(-12, -130, -8, 90, 7, 708, 'rgba(220,235,255,0.35)', 1.5);
      g.restore();
      g.restore();
    },
    planet: (h) => {              // an engraved ringed planet
      const r = h * 0.36;
      g.save(); g.translate(0, -h / 2);
      inkCircle(0, 0, r * 1.75, { sx: 1, sy: 0.28, w: 4, color: '#8a6a3a', seed: 711, passes: 1, over: 0 });
      paperCircle(0, 0, r, '#d9b77e', { seed: 712, depth: 1 });
      for (let i = -2; i <= 2; i++) ink([[-r * Math.sqrt(1 - (i / 3) ** 2) * 0.95, i * r / 3], [r * Math.sqrt(1 - (i / 3) ** 2) * 0.95, i * r / 3 + 4]], { w: 2, color: 'rgba(90,60,30,0.45)', seed: 713 + i, passes: 1, step: 20 });
      ink([[-r * 1.7, 8], [-r * 0.9, r * 0.28], [r * 0.9, r * 0.28], [r * 1.7, -6]], { w: 4, color: '#8a6a3a', seed: 719, passes: 1, step: 20 });
      g.restore();
    },
    rock_layers: (h) => {         // a block of strata, oldest at the bottom
      const w = h * 1.5, cols = ['#c9b48a', '#a98a62', '#d8c7a0', '#8f7454', '#b99f78'], n = cols.length, lh = h / n;
      cols.forEach((c, i) => paperRect(0, Math.round(-h + lh * (i + 0.5)), Math.round(w - i * 4), Math.round(lh + 2), c, { seed: 720 + i, depth: i === n - 1 ? 1.2 : 0.2, boil: false }));
      for (let i = 1; i < n; i++) ink([[-w / 2, -h + lh * i], [-w / 6, -h + lh * i - 5], [w / 6, -h + lh * i + 4], [w / 2, -h + lh * i]], { w: 2.5, color: 'rgba(43,36,32,0.55)', seed: 730 + i, passes: 1, step: 24 });
      const r = rng(739); g.fillStyle = 'rgba(43,36,32,0.35)';
      for (let i = 0; i < 70; i++) { g.beginPath(); g.arc((r() - 0.5) * w * 0.94, -r() * h * 0.96, 1 + r() * 2, 0, TAU); g.fill(); }
    },
    flower_pot: (h) => {          // a terracotta pot of soil (nothing in it)
      const k = h / 150;
      g.save(); g.scale(k, k);
      paperPiece(SH.pot, 0, 0, '#b5643c', { seed: 741, depth: 1.2 });
      paperRect(0, -122, 140, 28, '#c9794c', { seed: 742, depth: 0.6, boil: false });
      paperRect(0, -138, 112, 12, '#5a3b22', { seed: 743, depth: 0, boil: false });
      hatch(-40, -100, -30, -8, 6, 744, 'rgba(60,25,10,0.35)', 2);
      g.restore();
    },
  };
  const PROPBOX = { telescope: [240, 320], planet: [300, 220], rock_layers: [260, 170], flower_pot: [170, 160] };
  function prop(name, x, y, h, o = {}) {
    const s = o.s ?? 1; if (s <= 0.001) return;
    if (IMG[name]) { cut(name, x, y, h, { ay: 1, s, rot: o.rot || 0, flip: !!o.flip, seed: o.seed || 7, depth: 1.1 }); return; }
    const [bw, bh] = PROPBOX[name], k = h / bh, w = bw * k;
    g.save(); g.translate(x, y); g.rotate(o.rot || 0); g.scale(s * (o.flip ? -1 : 1), s);
    layer('prop|' + name + '|' + Math.round(h), -w / 2 - 20, -h - 20, w + 40, h + 40, () => STAND[name](h), { zoom: 1 });
    g.restore();
  }
  /* small code-only doodles (the storyboard's wrench, rocket, atom, watering can, mortarboard, magnifier) */
  function wrench(x, y, rot, s = 1) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperPiece(SH.wrench, 0, 0, '#8d949b', { seed: 751, depth: 1 });
    paperCircle(0, -80, 30, '#8d949b', { seed: 752, depth: 1 });
    paperRect(0, -100, 22, 34, FC().blue, { seed: 753, depth: 0, boil: false });
    paperCircle(0, 80, 18, '#8d949b', { seed: 754, depth: 0.8 });
    ink([[-4, -40], [-4, 60]], { w: 2, color: 'rgba(43,36,32,0.45)', seed: 755, passes: 1 });
    g.restore();
  }
  function rocket(x, y, s = 1) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(0.5); g.scale(s, s);
    paperPiece(SH.fin, -18, 20, RED, { seed: 761, depth: 0.6, sx: -1 }); paperPiece(SH.fin, 18, 20, RED, { seed: 762, depth: 0.6 });
    paperPiece(SH.rocket, 0, 0, FC().cream, { seed: 763, depth: 1 });
    paperCircle(0, -30, 8, FC().blue, { seed: 764, depth: 0 });
    ink([[-8, 44], [0, 70], [8, 44]], { w: 5, color: '#e0a33a', seed: 765, passes: 1, step: 6 });
    hatch(-12, -40, -10, 30, 3, 766, 'rgba(43,36,32,0.3)', 1.5);
    g.restore();
  }
  function atom(x, y, s = 1) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    for (let k = 0; k < 3; k++) { g.save(); g.rotate(k * Math.PI / 3); inkCircle(0, 0, 54, { sx: 1, sy: 0.34, w: 3, color: FC().cream, seed: 771 + k, passes: 1, over: 0 }); g.restore(); }
    paperCircle(0, 0, 14, RED, { seed: 775, depth: 0.5 });
    g.restore();
  }
  function cap(x, y, s = 1) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    paperRect(0, 16, 64, 26, '#222831', { seed: 781, depth: 0.8, boil: false });
    paperPiece(SH.cap, 0, 0, '#2b2f38', { seed: 782, depth: 1 });
    ink([[0, 0], [40, 6], [52, 34]], { w: 3, color: '#e0a33a', seed: 783, passes: 1, step: 8 });
    paperCircle(52, 40, 6, '#e0a33a', { seed: 784, depth: 0 });
    g.restore();
  }
  function wateringCan(x, y, t, t0, s = 1) {
    if (s <= 0.001) return;
    const tip = E.inOutSine(P(t, t0, t0 + 0.5)) * -0.45;
    g.save(); g.translate(x, y); g.rotate(tip); g.scale(s, s);
    ink([[-40, -44], [-10, -86], [30, -44]], { w: 7, color: '#4f6f58', seed: 791, passes: 1, step: 8 });
    ink([[-50, 20], [-110, -26], [-122, -34]], { w: 9, color: '#4f6f58', seed: 792, passes: 1, step: 8 });
    paperPiece(SH.can, 0, 0, '#6f8f76', { seed: 793, depth: 1 });
    paperRect(0, -12, 90, 8, '#4f6f58', { seed: 794, depth: 0, boil: false });
    g.restore();
    // the water: drops from the spout to the soil while it pours (pure in t)
    if (t >= t0 + 0.4) {
      const sx = x - 118 * s, sy = y - 10 * s + 60 * (1 - s), end = t0 + 4.2;
      if (t < end) for (let k = 0; k < 7; k++) {
        const u = (((t - t0) * 1.6 + k / 7) % 1 + 1) % 1, dx = sx - 30 * u, dy = sy + 120 * u * u + 40 * u;
        g.fillStyle = 'rgba(160,205,240,0.9)'; g.beginPath(); g.ellipse(dx, dy, 4, 7, 0.2, 0, TAU); g.fill();
      }
    }
  }
  function magnifier(x, y, s, prog, seed) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    inkCircle(0, 0, 26, { w: 5, color: FC().cream, seed, passes: 1 });
    ink([[18, 18], [42, 44]], { w: 8, color: FC().cream, seed: seed + 1, passes: 1 });
    g.restore();
    if (prog > 0) tick(x, y, 0.55 * s, prog, seed + 2, '#9fe29a');
  }

  /* ======================================================================
     Staging: puppets, captions, chapter cards
     ====================================================================== */
  const POS = { max: { x: 250, y: 1088, h: 560 }, voss: { x: 1665, y: 1088, h: 620 } };
  const MAX_POINT = new Set(['s1_01', 's1_05', 's2_04', 's3_03', 's3_05', 's5_03']);
  const VOSS_POINT = new Set(['s2_01', 's2_03', 's3_01', 's3_02', 's3_04', 's4_02', 's4_03', 's5_01', 's5_02', 's5_04']);
  function cast(t, ctx, o = {}) {
    const cap_ = ctx.caption, id = cap_ ? cap_.id : '', who = cap_ ? cap_.who : '';
    const mp = who === 'max' && MAX_POINT.has(id) ? 'point' : 'cross';
    const vp = who === 'voss' && (VOSS_POINT.has(id) || ctx.kind === 'reply') ? 'point' : null;
    Puppet.draw('max', POS.max.x, POS.max.y, POS.max.h, { env: ctx.speaking('max'), facing: 1, t, pose: mp });
    Puppet.draw('voss', POS.voss.x + (o.vossDX || 0), POS.voss.y, POS.voss.h, { env: ctx.speaking('voss'), facing: -1, t, pose: vp });
    bubble(ctx);
  }
  /* caption bubbles: ≤ 2 rows (wider first, then paged at sentence ends, timed by the words), in the top band */
  const BS = () => (Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  function pages(l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size + '|' + l.dur.toFixed(2);
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HANDF}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const out = []; let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut_ = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut_ = j; break; } if (cut_ > 0) break; }
        if (cut_ > 0) b = cut_;
      }
      out.push([a, b]); a = b;
    }
    const res = out.map(([a_, b_], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a_].s - 0.15);
      return { t0, text: toks.slice(a_, b_).map(x => x.w).join(' '), rows: rowsOf(a_, b_), tokens: toks.slice(a_, b_).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    if (pageCache.size > 300) pageCache.clear();
    if (fontOk()) pageCache.set(key, res);
    return res;
  }
  function fit(l, w0, maxW) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  }
  const pageAt = (pg, lt) => { let k = 0; for (let i = 1; i < pg.length; i++) if (lt >= pg[i].t0) k = i; return pg[k]; };
  function bubble(ctx) {
    const l = ctx.caption; if (!l) return;
    const p = POS[l.who]; if (!p) return;
    const left = l.who === 'max', k = BS() / 40;
    const f = fit(l, Math.round(720 * k), Math.round(880 * k));
    const w = f.w, bh = f.h;
    let cx = left ? p.x - 70 + w / 2 : p.x + 70 - w / 2;
    const cy = Math.max(bh / 2 + 16, 80 + bh / 2);
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    const cap_ = { x: cx, y: cy, side: left ? 'left' : 'right', w };
    if (f.pages.length > 1) { const pg = pageAt(f.pages, ctx.t - l.start); Object.assign(cap_, { text: pg.text, tokens: pg.tokens, t0: pg.t0 }); }
    ctx.captionAt = cap_;
  }

  /* ---------- chapter cards: a blueprint sheet with a cream index card; it holds, then tears away to the scene ---------- */
  const CARD = { hold: 1.1, tear: 0.7, lead: 2.2 };
  function drawCard(i) {
    const seg = segs()[i]; if (!seg) return;
    const n = i + 1, C = FC();
    layer('card_bg|' + artKey('tex_blueprint'), 0, 0, 1920, 1080, () => {
      F().backdrop();
      paperRect(960, 540, 1560, 860, C.cream, { seed: 900, depth: 1.6, boil: false });
      g.save(); g.strokeStyle = 'rgba(90,150,190,0.28)'; g.lineWidth = 2; g.beginPath();
      for (let y = 250; y < 960; y += 58) { g.moveTo(200, y); g.lineTo(1720, y); }
      g.stroke(); g.strokeStyle = 'rgba(200,50,31,0.35)'; g.beginPath(); g.moveTo(330, 120); g.lineTo(330, 960); g.stroke(); g.restore();
      paperRect(960, 142, 1552, 46, C.law, { seed: 901, depth: 0, boil: false });
      tape(230, 118, 190, -0.55, undefined, 902); tape(1690, 118, 190, 0.55, undefined, 903);
      // the two jobs, small, in the bottom corners (no arrow between them)
      F().card('law', 470, 850, 230, 140, { title: 'LAW', body: 'what', seed: 905, rot: -0.05 });
      F().card('theory', 1450, 850, 230, 140, { title: 'THEORY', body: 'why', seed: 907, rot: 0.05 });
    }, { zoom: 0.5 });
    txt('CHAPTER', 960, 280, 50, { font: TYPE, color: C.navy });
    ransom(String(n), 960, 430, 200, -1e6, { seed: 700 + n });
    const title = String(seg.chapter || seg.id);
    g.save(); const size = F().fitFont(title, 1150, 124, HANDF); g.restore();
    hand(title, 960, 640, size, -1e6, { dur: 0.01, color: C.navy, seed: 940 + n, rot: -0.02 });
    const w = tw(title, `${size}px ${HANDF}`);
    ink([[960 - w / 2 - 10, 716], [960, 708], [960 + w / 2 + 16, 720]], { w: 8, color: RED, seed: 950 + n, passes: 2 });
    hand('~ ' + String((Game.lesson && Game.lesson.title) || '').toLowerCase() + ' ~', 960, 860, 40, -1e6, { dur: 0.01, color: 'rgba(37,60,90,0.7)', seed: 960 + n });
  }
  function chapterSfx(part) { return part.kind === 'lines' ? [{ name: 'tear', t: CARD.hold, gain: 0.5 }] : []; }

  /* ======================================================================
     the numbers: NASA's planets and q3 through the model (core-local/nos.js)
     ====================================================================== */
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const N = window.Nos;
    const planets = N.PLANETS.map(p => { const y = N.years(p.T); return { name: p.name, a: p.a, years: y, a3: p.a ** 3, T2: y * y, ratio: N.ratio(p) }; });
    const ratios = planets.map(p => p.ratio), rmin = Math.min(...ratios), rmax = Math.max(...ratios);
    const q = segById('s3').question, q3 = N.q3(q.working.given.a);
    const wk = working(q), checks = [];
    if (wk) {
      if (Math.abs(wk.steps[0].v - q3.a3) > 1e-6) checks.push(`q3 step 1 ${wk.steps[0].v} ≠ Nos.q3 a3 ${q3.a3}`);
      if (Math.abs(wk.steps[1].v - q3.T) > 1e-6) checks.push(`q3 step 2 ${wk.steps[1].v} ≠ Nos.q3 T ${q3.T}`);
      if (Math.abs(wk.steps[2].v - q3.nasaYears) > 1e-9) checks.push(`q3 step 3 ${wk.steps[2].v} ≠ NASA ${q3.nasaYears}`);
    }
    if (String(q.options[q.correct]).indexOf(String(Math.round(q3.T))) < 0) checks.push(`q3: the correct option does not say ${Math.round(q3.T)}`);
    if (!(rmin > 0.99 && rmax < 1.01)) checks.push(`ratios ${rmin}..${rmax} are not about 1`);
    if (checks.length) console.warn('[video] model checks', checks);
    NUMS = { planets, rmin, rmax, q3, predicted: N.fmt(q3.T, 4), nasa: String(q3.nasaYears), checks, rminText: N.fmt(rmin, 3), rmaxText: N.fmt(rmax, 3) };
    return NUMS;
  }
  const item = id => window.Nos.ITEMS[id];
  const term = id => window.Nos.TERMS[id];

  /* ======================================================================
     THE WORKING, STEP BY STEP (like lessons/blue-eyes): q3's `working` ({given, steps}) written on a lab card as numbered
     steps in time with Dr. Voss's reply, on the right AND every wrong branch, then the answer line (the correct option).
     Texts: Bank.fill; values: Bank.eval + Bank.format.
     ====================================================================== */
  const WK = new Map();
  function working(q) {
    if (WK.has(q.id)) return WK.get(q.id);
    const W_ = q.working; if (!W_) { WK.set(q.id, null); return null; }
    const steps = W_.steps.map((st, i) => {
      const filled = Bank.fill(st.text, W_.given).trim(), v = Bank.eval(st.formula, W_.given), value = Bank.format(v, st.format || 'text');
      let label = '', maths;
      if (/:$/.test(filled)) { label = filled.slice(0, -1).trim(); maths = value; }
      else maths = filled + ' ' + value;
      return { i, n: i + 1, label, maths, value, v, text: filled + ' ' + value };
    });
    const opt = String(q.options[q.correct]), wk = { steps, answer: opt.includes(':') ? opt : 'Answer: ' + opt };
    WK.set(q.id, wk);
    return wk;
  }
  /* per branch, the reply word each step lands on (null = the start of the reply) */
  const STEPCUE = {
    q3: { q3_right: ['Cube', 'square', 'NASA'], q3_wrong0: ['Cube', 'square', 'About'], q3_wrong1: ['cube', 'root', 'About'] },
  };
  function stepTimes(c, q) {
    const wk = working(q); if (!wk) return null;
    const tbl = STEPCUE[q.id] || {}, out = [], cardIn = replyStart(c, q) + 0.3;
    let prev = cardIn;
    wk.steps.forEach((st, i) => {
      let cue = Infinity;
      for (const id in tbl) { const w = tbl[id][i]; if (w == null) { cue = Math.min(cue, c.L(id)); continue; } const [ww, nn] = Array.isArray(w) ? w : [w, 1]; cue = Math.min(cue, c.W(id, ww, nn)); }
      const t0 = Math.max(cue, prev + (i ? 0.75 : 0.25));
      out.push(t0); prev = t0;
    });
    const hi = prev + 0.7;
    return { steps: out, hi, answer: hi + 0.35, cardIn };
  }
  const WL = { top: 74, label: 28, lrow: 34, maths: 46, mrow: 56, ans: 42 };
  const WLAY = new Map();
  function wkLayout(q, W) {
    const key = q.id + '|' + W; if (WLAY.has(key)) return WLAY.get(key);
    const wk = working(q);
    const rows = wk.steps.map(st => {
      const lines = st.label ? wrapLines(st.label, W - 110, WL.label, TYPE) : [];
      const mlines = wrapLines(st.maths, W - 100, WL.maths);
      return { lines, mlines, h: lines.length * WL.lrow + mlines.length * WL.mrow + 10 };
    });
    const ans = wrapLines(wk.answer, W - 110, WL.ans);
    const H = WL.top + rows.reduce((a, r) => a + r.h, 0) + 12 + ans.length * 48 + 26;
    const L = { rows, ans, H };
    if (fontOk()) WLAY.set(key, L);
    return L;
  }
  /* the card: X, Y top-left, W wide; returns {h} */
  function workCard(q, X, Y, W, tm) {
    const wk = working(q); if (!wk || !tm) return null;
    const L = wkLayout(q, W), H = L.H, C = FC();
    const geom = { h: H };
    const s = pop(T, tm.cardIn, 0.3, 1.4);
    if (s <= 0) return geom;
    g.save(); g.translate(X + W / 2, Y + H / 2); g.rotate(-0.008); g.scale(s, s);
    paperRect(0, 0, W, H, C.graph, { seed: 560, depth: 1.4, boil: false });
    paperRect(0, -H / 2 + 20, W - 8, 30, C.law, { seed: 562, depth: 0, boil: false });
    g.strokeStyle = C.graphLine; g.lineWidth = 1; g.beginPath();
    for (let xx = -W / 2 + 14; xx < W / 2 - 8; xx += 26) { g.moveTo(xx, -H / 2 + 38); g.lineTo(xx, H / 2 - 8); }
    for (let yy = -H / 2 + 50; yy < H / 2 - 8; yy += 26) { g.moveTo(-W / 2 + 8, yy); g.lineTo(W / 2 - 8, yy); }
    g.stroke();
    tape(-W / 2 + 30, -H / 2 + 8, 90, -0.6, undefined, 561);
    g.restore();
    if (T < tm.cardIn + 0.2) return geom;
    txt('THE WORKING', X + W / 2, Y + 21, 22, { font: SLAB, color: C.cream });
    let y = Y + WL.top;
    wk.steps.forEach((st, i) => {
      const t0 = tm.steps[i], r = L.rows[i];
      r.lines.forEach((ln, j) => hand(ln, X + 70, y + WL.lrow / 2 + j * WL.lrow, WL.label, t0, { align: 'left', color: PENCIL, font: TYPE, seed: 570 + i * 5 + j, dur: 0.3 }));
      const my0 = y + r.lines.length * WL.lrow + WL.mrow / 2;
      hand(`${st.n}.`, X + 24, my0, 40, t0, { align: 'left', color: PENCIL, seed: 575 + i, dur: 0.15 });
      r.mlines.forEach((ml, j) => {
        const my = my0 + j * WL.mrow, last = j === r.mlines.length - 1;
        if (last && i === wk.steps.length - 1 && T > tm.hi) {
          const f = `${WL.maths}px ${HANDF}`, x0 = X + 70 + tw(ml.slice(0, ml.lastIndexOf(st.value)), f), vw = tw(st.value, f);
          g.save(); g.globalAlpha *= 0.5 * P(T, tm.hi, tm.hi + 0.3); g.fillStyle = '#f2c94c'; g.fillRect(x0 - 8, my - 26, (vw + 16) * E.outCubic(P(T, tm.hi, tm.hi + 0.3)), 50); g.restore();
        }
        hand(ml, X + 70, my, WL.maths, t0 + 0.2 + j * 0.35, { align: 'left', color: NAVY, seed: 580 + i * 3 + j, dur: Math.min(0.9, 0.2 + ml.length * 0.03) });
      });
      y += r.h;
    });
    y += 12;
    L.ans.forEach((ln, j) => hand(ln, X + 70, y + 24 + j * 48, WL.ans, tm.answer + j * 0.2, { align: 'left', color: RED, seed: 590 + j, dur: 0.4 }));
    const shown = tm.steps.filter(t0 => T >= t0 + 0.2).length;
    Vid.lastSteps = { q: q.id, n: wk.steps.length, shown, answer: T >= tm.answer, values: wk.steps.map(x => x.value) };
    if (docked() && shown > 0) Dock.text('steps-' + q.id, ['The working:'].concat(wk.steps.filter((x, i) => T >= tm.steps[i] + 0.2).map(x => `${x.n}. ${x.text}`), T >= tm.answer ? [wk.answer] : []), { cls: 'note' });
    return geom;
  }
  const stepSfx = tm => (tm ? [{ name: 'paper', t: tm.cardIn, gain: 0.3 }, ...tm.steps.map(t0 => ({ name: 'scribble', t: t0 + 0.2, gain: 0.18 })), { name: 'ding', t: tm.answer, gain: 0.25 }] : []);

  /* ======================================================================
     The two NASA plots (Fair.graph): T (years) vs a (AU), then T² vs a³ (thousands) with the fitted line T² = a³
     ====================================================================== */
  const LABEL1 = new Set(['Jupiter', 'Saturn', 'Uranus']);
  function plotTa(x, y, w, h, t0, o = {}) {
    const N = nums();
    const pts = N.planets.map(p => ({ x: p.a, y: p.years, label: LABEL1.has(p.name) ? p.name : '', hi: false }));
    const G = F().graph(x, y, w, h, { xmax: 32, ymax: 180, xticks: [0, 10, 20, 30], yticks: [0, 50, 100, 150], xLabel: 'distance a (AU)', yLabel: 'year T (years)', points: pts, pointsT0: t0, seed: 61, title: o.title || 'NASA: the eight planets' });
    const nep = N.planets[N.planets.length - 1], np = G.at(nep.a, nep.years);
    if (T >= t0 + 0.12 * 7 && !o.noNep) hand('Neptune', np.x - 18, np.y + 30, 24, -1e6, { dur: 0.01, align: 'right', color: INK, seed: 66 });
    return G;
  }
  function plotT2a3(x, y, w, h, t0, lineProg) {
    const N = nums();
    const pts = N.planets.map(p => ({ x: p.a3 / 1000, y: p.T2 / 1000, label: p.name === 'Uranus' ? p.name : '' }));
    const G = F().graph(x, y, w, h, { xmax: 30, ymax: 30, xticks: [0, 10, 20, 30], yticks: [0, 10, 20, 30], xLabel: 'distance cubed, a³ (thousands)', yLabel: 'year squared, T² (thousands)', points: pts, pointsT0: t0, line: v => v, prog: lineProg, seed: 71, title: 'NASA: the eight planets' });
    const nep = N.planets[N.planets.length - 1], np = G.at(nep.a3 / 1000, nep.T2 / 1000);
    if (T >= t0 + 0.12 * 7) hand('Neptune', np.x - 18, np.y + 30, 24, -1e6, { dur: 0.01, align: 'right', color: INK, seed: 76 });
    return G;
  }
  /* the inner four, zoomed (they crowd the corner of the big plot) */
  function innerZoom(x, y, s, t0) {
    if (s <= 0.001) return;
    const N = nums();
    const pts = N.planets.slice(0, 4).map(p => ({ x: p.a3, y: p.T2, label: p.name === 'Earth' || p.name === 'Mars' ? p.name : '' }));
    g.save(); g.translate(x, y); g.rotate(0.02); g.scale(s, s);
    F().graph(0, 0, 310, 240, { xmax: 4, ymax: 4, xticks: [0, 2, 4], yticks: [0, 2, 4], points: pts, pointsT0: t0, line: v => v, prog: 1, seed: 81 });
    g.restore();
    tag('the inner four, zoomed in', x + 10, y - 134 * s, 24, s, { seed: 82, rot: -0.02, color: FC().cream });
  }

  /* ======================================================================
     the five scenes (BODY[id](t, part, ctx, c) draws the stage; S[id] wraps it with the chapter card)
     ====================================================================== */
  const BODY = {};
  const Vid = window.TheoryVideo = { BODY, nums, working, STEPCUE, layers: LY, lastSteps: null, lastDot: null, lastFlip: null };

  /* ---- s1 "Graduation day": Max's poster, the Science Ladder, the ladder tears into LAW = WHAT / THEORY = WHY ---- */
  const B1 = { x: 960, y: 530, w: 1000, h: 560 };
  const LAD = { x: 960, top: 360, bot: 780, half: 92 };
  const RUNG = { law: 420, theory: 590, hypo: 740 };
  function ladder(t, tm) {
    const C = FC();
    const drawIt = () => {
      ink([[LAD.x - LAD.half, LAD.bot], [LAD.x - LAD.half + 4, LAD.top]], { w: 8, color: NAVY, seed: 401, passes: 2, step: 20, prog: P(t, tm.ladder, tm.ladder + 0.4) });
      ink([[LAD.x + LAD.half, LAD.bot], [LAD.x + LAD.half - 4, LAD.top]], { w: 8, color: NAVY, seed: 402, passes: 2, step: 20, prog: P(t, tm.ladder + 0.1, tm.ladder + 0.5) });
      for (let k = 0; k < 9; k++) { const yy = LAD.bot - 20 - k * 48; if (t >= tm.ladder + 0.3 + k * 0.04) ink([[LAD.x - LAD.half, yy], [LAD.x + LAD.half, yy - 3]], { w: 6, color: NAVY, seed: 410 + k, passes: 1, step: 20 }); }
      if (t >= tm.hypo) tag('HYPOTHESIS', LAD.x, RUNG.hypo, 38, pop(t, tm.hypo, 0.3), { font: SLAB, seed: 421, rot: -0.02, color: C.hypo, ink: C.hypoInk });
      if (t >= tm.theory) tag('THEORY', LAD.x, RUNG.theory, 42, pop(t, tm.theory, 0.3), { font: SLAB, seed: 422, rot: 0.02, color: C.amber });
      if (t >= tm.law) tag('LAW', LAD.x, RUNG.law, 48, pop(t, tm.law, 0.3), { font: SLAB, seed: 423, rot: -0.03, color: C.law, ink: C.cream });
      if (t >= tm.law) inkArrow(LAD.x + LAD.half + 40, RUNG.hypo - 20, LAD.x + LAD.half + 40, RUNG.law + 30, { w: 5, color: RED, seed: 424, prog: P(t, tm.law + 0.2, tm.law + 0.7), bulge: -18, head: 20 });
      cap(LAD.x + 6, RUNG.law - 58, pop(t, tm.cap, 0.35, 2.2));
    };
    // the reply: the ladder tears in two (the top half flies up-left, the bottom half down-right) and fades
    const u = E.inCubic(P(t, tm.tear, tm.tear + 0.7));
    if (u <= 0) { drawIt(); return; }
    if (u >= 1) return;
    const mid = (RUNG.theory + RUNG.law) / 2 + 30;
    for (const [dy, clipY0, clipY1, rot, dx] of [[-1, 0, mid, -0.35, -1], [1, mid, 1080, 0.3, 1]]) {
      g.save(); g.globalAlpha *= 1 - u;
      g.beginPath(); g.rect(0, clipY0, 1920, clipY1 - clipY0); g.clip();
      g.translate(LAD.x + dx * u * 160, mid + dy * u * 120); g.rotate(rot * u); g.translate(-LAD.x, -mid);
      drawIt();
      g.restore();
    }
  }
  BODY.s1 = function (t, part, ctx, c) {
    const C = FC(), q = part.seg.question, ans = ansOf(ctx, part), rs = replyStart(c, q);
    bg(930);
    const tm = {
      board: c.L('s1_01') - 0.3, title: c.W('s1_01', 'Ladder'), ladder: c.W('s1_01', 'Ladder') + 0.2,
      hypo: c.W('s1_01', 'Hypothesis'), theory: c.W('s1_01', 'theory'), law: c.W('s1_01', 'law'),
      sticky: c.W('s1_02', 'Plate'), cap: c.W('s1_02', 'Cap'), voss: c.L('s1_03'),
      tear: rs + 0.35,
      why: branch(c, { q1_right: 'explains', q1_wrong0: 'stronger', q1_wrong2: 'explain' }),
      what: branch(c, { q1_right: 'describes', q1_wrong0: 'describes', q1_wrong2: 'describe' }),
      jobs: c.W('s1_04', 'Different'), cards: c.W('s1_05', 'cards'), water: c.W('s1_06', 'water'), never: c.W('s1_06', 'never'),
    };
    F().table(960, 842, 1120, { seed: 21, s: pop(t, tm.board - 0.2, 0.4, 1.6) });
    const bs = pop(t, tm.board, 0.45, 1.6);
    F().board(B1.x, B1.y, B1.w, B1.h, { title: t >= tm.title ? 'THE SCIENCE LADDER' : '', seed: 51, s: bs, art: true });
    if (bs > 0.98) {
      hand('by Max, grade 7', B1.x - 360, B1.y + 220, 30, -1e6, { dur: 0.01, color: PENCIL, seed: 431, rot: -0.03 });
      ladder(t, tm);
      // the sticky on the left panel (s1_02)
      sticky(['PLATE TECTONICS:', 'just a theory', '(for now)'], B1.x - 360, 405, tm.sticky, { w: 250, size: 32, seed: 440, rot: -0.05, lineT: [tm.sticky + 0.1, tm.sticky + 0.5, tm.sticky + 0.9] });
      // the reply (every branch): two cards side by side, no arrow between them
      wcard('law', B1.x - 165, 620, 300, 210, { title: 'LAW', body: 'describes WHAT happens', stamp: true, seed: 450, rot: -0.02, t0: tm.what, bodyT: tm.what + 0.2, size: 34 });
      wcard('theory', B1.x + 165, 624, 300, 210, { title: 'THEORY', body: 'explains WHY it happens', stamp: true, seed: 452, rot: 0.02, t0: tm.why, bodyT: tm.why + 0.2, size: 34 });
      // a wrong answer's idea on a pink sticky, struck (reply only)
      if (part.kind === 'reply' && ans !== q.correct && ans != null) {
        const lines = ans === 0 ? ['ENOUGH EVIDENCE', '= A LAW?'] : ['100 YEARS', '= A LAW?'];
        sticky(lines, B1.x + 360, 400, rs + 0.1, { w: 250, size: 34, color: PINK, seed: 460 + ans, strike: branch(c, { q1_wrong0: 'never', q1_wrong2: 'Waiting' }) + 0.2, rot: 0.05 });
      }
      if (t >= tm.jobs) tag('DIFFERENT JOBS, NOT RANKS', B1.x, 760, 36, pop(t, tm.jobs, 0.35), { font: HANDF, seed: 462, rot: -0.015, color: C.cream, ink: RED });
      if (t >= tm.cards) hand('(no arrow)', B1.x, 490, 30, tm.cards + 0.3, { color: PENCIL, seed: 463, dur: 0.3 });
    }
    // the watered pot (s1_06): you can water a theory all year; it never sprouts a law
    const ps = pop(t, tm.water - 0.3, 0.35);
    if (ps > 0) {
      prop('flower_pot', 1360, 834, 150, { s: ps });
      tag('THEORY', 1360, 770, 26, ps, { font: SLAB, seed: 470, color: C.amber, rot: 0.03 });
      wateringCan(1478, 700, t, tm.water, ps);
      if (t >= tm.never) tag('no law sprouting', 1300, 890, 28, pop(t, tm.never + 0.2, 0.35), { seed: 471, rot: -0.03, color: C.cream, ink: RED });
    }
    if (docked()) {
      const L = [];
      if (t >= tm.title) L.push("Max's poster: THE SCIENCE LADDER");
      if (t >= tm.law) L.push('HYPOTHESIS, then THEORY, then LAW (with a graduation cap)');
      if (t >= tm.sticky) L.push('Sticky: PLATE TECTONICS: just a theory (for now)');
      if (t >= tm.what) L.push('LAW: describes WHAT happens.');
      if (t >= tm.why) L.push('THEORY: explains WHY it happens.');
      if (t >= tm.jobs) L.push('Different jobs, not ranks.');
      if (t >= tm.never) L.push('Watering a theory never sprouts a law.');
      if (L.length) Dock.text('s1-poster', L, { cls: 'note' });
    }
    // Dr. Voss walks up with her clipboard (s1_03)
    const vin = E.outCubic(P(t, tm.voss - 1.0, tm.voss - 0.2));
    cast(t, ctx, { vossDX: (1 - vin) * 560 });
    Vid.lastTear = { tear: t >= tm.tear + 0.7, law: t >= tm.what, theory: t >= tm.why };
  };
  S.s1 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.2, after: 3.4 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part), rs = replyStart(c, q);
      out.push({ name: fx('poster_thump', 'thunk'), t: c.L('s1_01') - 0.3, gain: 0.35 }, { name: 'scribble', t: c.W('s1_01', 'Ladder') + 0.2, gain: 0.2 });
      ['Hypothesis', 'theory', 'law'].forEach(w => out.push({ name: 'pop', t: c.W('s1_01', w), gain: 0.22 }));
      out.push({ name: 'paper', t: c.W('s1_02', 'Plate'), gain: 0.25 }, { name: 'boing', t: c.W('s1_02', 'Cap'), gain: 0.25 }, { name: 'whoosh', t: c.L('s1_03') - 1.0, gain: 0.2 });
      out.push({ name: 'tear', t: rs + 0.35, gain: 0.45 });
      out.push({ name: fx('card_flip', 'paper'), t: branch(c, { q1_right: 'explains', q1_wrong0: 'stronger', q1_wrong2: 'explain' }), gain: 0.3 });
      out.push({ name: fx('card_flip', 'paper'), t: branch(c, { q1_right: 'describes', q1_wrong0: 'describes', q1_wrong2: 'describe' }), gain: 0.3 });
      out.push({ name: fx('chalk_squeak', 'scribble'), t: branch(c, { q1_wrong0: 'never', q1_wrong2: 'Waiting' }) + 0.2, gain: 0.3 });
      out.push({ name: 'paper', t: c.W('s1_04', 'Different'), gain: 0.25 }, { name: 'sparkle', t: c.W('s1_06', 'water') + 0.4, gain: 0.2 }, { name: 'wahwah', t: c.W('s1_06', 'never') + 0.2, gain: 0.2 });
      return out;
    },
  };

  /* ---- s2 "What and why": the wrench drop, the gravitation card, Newton's words, general relativity, three cards ---- */
  const S2 = { wrench: { x: 1500, y0: 520, y1: 900 }, law: { x: 710, y: 350, w: 520, h: 210 }, strip: { x: 790, y: 610, w: 700, h: 250 }, gr: { x: 1235, y: 350, w: 430, h: 210 }, grid: { x: 1300, y: 570, w: 320, h: 170 } };
  const Q2 = [
    { id: 'superposition', x: 640, kind: 'law' },
    { id: 'plateTectonics', x: 960, kind: 'theory' },
    { id: 'conservationEnergy', x: 1280, kind: 'law' },
  ];
  const Q2Y = 540, Q2W = 310, Q2H = 380;
  function grDoodle(t, x, y, s, seed = 480) {
    if (s <= 0.001) return;
    const G = S2.grid, C = FC();
    g.save(); g.translate(x, y); g.scale(s, s);
    layer('grgrid', -G.w / 2 - 10, -G.h / 2 - 10, G.w + 20, G.h + 20, () => {
      paperRect(0, 0, G.w, G.h, C.graph, { seed, depth: 1.2, boil: false });
      const dip = (px, py) => 46 * Math.exp(-((px * px) / 9000 + (py * py) / 2200));
      for (let r = -3; r <= 3; r++) { const pts = []; for (let k = 0; k <= 24; k++) { const px = -G.w / 2 + 16 + k * (G.w - 32) / 24, py = r * 24; pts.push([px, py + dip(px, py)]); } ink(pts, { w: 2, color: 'rgba(37,60,90,0.6)', seed: seed + 10 + r, passes: 1, step: 12, amp: 0.4 }); }
      for (let k = 0; k <= 12; k++) { const px = -G.w / 2 + 16 + k * (G.w - 32) / 12, pts = []; for (let r = -3; r <= 3; r++) { const py = r * 24; pts.push([px, py + dip(px, py)]); } ink(pts, { w: 2, color: 'rgba(37,60,90,0.6)', seed: seed + 30 + k, passes: 1, step: 12, amp: 0.4 }); }
    }, { zoom: 1 });
    paperCircle(0, 28, 26, C.amber, { seed: seed + 50, depth: 0.8 });
    // Mercury: a tiny planet going round (pure in t)
    const a = t * 1.3;
    paperCircle(Math.cos(a) * 92, 28 + Math.sin(a) * 30, 8, '#8d949b', { seed: seed + 51, depth: 0.4 });
    g.restore();
    hand('Mercury', x + Math.cos(a) * 92 * s + 14, y + (28 + Math.sin(a) * 30) * s - 20, 22, -1e6, { dur: 0.01, align: 'left', color: INK, seed: seed + 52 });
  }
  BODY.s2 = function (t, part, ctx, c) {
    const C = FC(), q = part.seg.question, ans = ansOf(ctx, part), rs = replyStart(c, q);
    bg(930);
    const tm = {
      wrench: c.W('s2_01', 'wrench'), law: c.W('s2_01', 'gravitation'), math: c.W('s2_01', 'math'),
      strip: c.W('s2_02', 'wrote'), frame: c.W('s2_02', 'frame'), described: c.W('s2_02', 'described'), explain: c.W('s2_02', 'explain'),
      gr: c.W('s2_03', 'relativity'), bent: c.W('s2_03', 'bent'), merc: c.W('s2_03', 'Mercury'),
      quiz: c.W('s2_04', 'Quiz'),
      flipWhy: branch(c, { q2_right: 'Moving', q2_wrong0: 'why', q2_wrong2: 'Plate' }),
      flipWhat: branch(c, { q2_right: 'describe', q2_wrong0: 'describes', q2_wrong2: 'describes' }),
      noMath: c.W('s2_05', 'math'), eq: c.W('s2_06', 'equation'), older: c.W('s2_06', 'older'),
    };
    // the wrench: dropped (s2_01), a quick ink streak, a small bounce on the floor
    const W = S2.wrench, fall = P(t, tm.wrench, tm.wrench + 0.5), land = tm.wrench + 0.5;
    const out = gone(t, tm.quiz - 0.2);
    if (t >= tm.wrench - 0.4 && out > 0) alpha(out, () => {
      const y = lerp(W.y0, W.y1, fall * fall) - (t > land ? 26 * Math.sin(Math.PI * P(t, land, land + 0.3)) : 0);
      if (t < land + 0.5) alpha(1 - P(t, land, land + 0.5), () => { for (let k = -1; k <= 1; k++) ink([[W.x + k * 18, W.y0 - 40], [W.x + k * 18, y - 60]], { w: 3, color: 'rgba(240,248,255,0.8)', seed: 490 + k, passes: 1, step: 30 }); });
      wrench(W.x, y, t < land ? -0.3 + fall * 2.2 : 1.9, pop(t, tm.wrench - 0.4, 0.3));
    });
    if (out > 0) alpha(out, () => {
      // the blue law card (s2_01), its line typed on
      const L = S2.law, it = item('gravitation');
      wcard('law', L.x, L.y, L.w, L.h, { title: 'LAW OF UNIVERSAL GRAVITATION', body: GRAV_CARD, stamp: t >= tm.math, seed: 500, rot: -0.015, t0: tm.law, bodyT: tm.law + 0.3, size: 34 });
      // Newton's sentence on a parchment strip (s2_02)
      const St = S2.strip, ss = pop(t, tm.strip, 0.4, 1.5);
      if (ss > 0) {
        g.save(); g.translate(St.x, St.y); g.rotate(0.012); g.scale(ss, ss);
        paperRect(0, 0, St.w, St.h, '#eadbb5', { seed: 510, torn: true, depth: 1.4, boil: false });
        tape(-St.w / 2 + 40, -St.h / 2 + 8, 90, -0.5, undefined, 511); tape(St.w / 2 - 40, -St.h / 2 + 8, 90, 0.5, undefined, 512);
        const ql = wrapLines('"' + NEWTON.quote + '"', St.w - 70, 30, TYPE);
        ql.forEach((l, j) => hand(l, -St.w / 2 + 34, -St.h / 2 + 40 + j * 38, 30, tm.strip + 0.2 + j * 0.5, { align: 'left', font: TYPE, color: INK, seed: 513 + j, dur: 0.5 }));
        hand('~ ' + NEWTON.by, St.w / 2 - 30, St.h / 2 - 30, 28, tm.strip + 0.4 + ql.length * 0.5, { align: 'right', color: '#6b4424', seed: 519, dur: 0.4 });
        g.restore();
        if (t >= tm.frame) {
          // underline "I frame no hypotheses."
          const ql2 = wrapLines('"' + NEWTON.quote + '"', St.w - 70, 30, TYPE), last = ql2[ql2.length - 1], k = last.indexOf('I frame');
          if (k >= 0) {
            const lx = St.x - St.w / 2 + 34 + tw(last.slice(0, k), `30px ${TYPE}`), lw = tw(last.slice(k), `30px ${TYPE}`), ly = St.y - St.h / 2 + 40 + (ql2.length - 1) * 38 + 20;
            ink([[lx, ly], [lx + lw, ly + 2]], { w: 5, color: RED, seed: 520, prog: P(t, tm.frame, tm.frame + 0.4), passes: 1, step: 20 });
          }
        }
        if (t >= tm.described) tag('described the pull', St.x - 190, St.y + St.h / 2 + 30, 30, pop(t, tm.described, 0.35), { seed: 521, rot: -0.03, color: C.law, ink: C.cream });
        if (t >= tm.explain) tag("didn't explain it", St.x + 190, St.y + St.h / 2 + 30, 30, pop(t, tm.explain, 0.35), { seed: 522, rot: 0.03, color: PINK });
      }
      // general relativity (s2_03): the amber card, a stretched-grid doodle, tiny Mercury
      const G = S2.gr, it2 = item('generalRelativity');
      wcard('theory', G.x, G.y, G.w, G.h, { title: `GENERAL RELATIVITY (${YEARS.relativity})`, body: 'a theory that explains gravity as bent space and time', stamp: t >= tm.bent, seed: 530, rot: 0.02, t0: tm.gr, bodyT: tm.gr + 0.3, size: 32 });
      void it2;
      grDoodle(t, S2.grid.x, S2.grid.y, pop(t, tm.bent, 0.4, 1.6));
    });
    if (docked()) {
      const L = [];
      if (t >= tm.law) L.push('LAW OF UNIVERSAL GRAVITATION: ' + item('gravitation').kid + '.');
      if (t >= tm.strip) L.push(`"${NEWTON.quote}" ${NEWTON.by}`);
      if (t >= tm.gr) L.push(`GENERAL RELATIVITY (${YEARS.relativity}): a theory that explains gravity as bent space and time.`);
      if (L.length && t < tm.quiz) Dock.text('s2-cards', L, { cls: 'note' });
    }
    // the three cards (s2_04 → the question → the reply flips them to their jobs)
    if (t >= tm.quiz) {
      const flips = { what: E.inOutSine(P(t, tm.flipWhat, tm.flipWhat + 0.5)), why: E.inOutSine(P(t, tm.flipWhy, tm.flipWhy + 0.5)) };
      Q2.forEach((k, i) => {
        const it = item(k.id), u = k.kind === 'law' ? flips.what : flips.why;
        const front = { kind: 'plain', title: it.short.toUpperCase(), body: it.name, stamp: false };
        const back = { kind: k.kind, title: it.short.toUpperCase(), body: (k.kind === 'theory' ? 'WHY: ' : '') + it.kid, stamp: true };
        const s = pop(t, tm.quiz + 0.1 + i * 0.15, 0.35, 1.8);
        flipCard(front, back, k.x, Q2Y, Q2W, Q2H, u, { seed: 540 + i * 7, rot: (i - 1) * 0.02, s, size: 30 });
        if (k.kind === 'theory' && u >= 1) tick(k.x + Q2W / 2 + 6, Q2Y - Q2H / 2 - 40, 1.1, P(t, tm.flipWhy + 0.5, tm.flipWhy + 0.8), 548, '#3f8f3a');
      });
      Vid.lastFlip = { what: flips.what, why: flips.why };
      // a wrong pick: the note under the card it picked (reply only)
      if (part.kind === 'reply' && ans !== q.correct && ans != null) {
        const k = Q2[ans], note = String(q.notes[ans] || '');
        const nt = rs + 0.3;
        if (t >= nt) {
          const bw = Q2W / 2 + 14, bh = Q2H / 2 + 14;
          ink([[k.x - bw, Q2Y - bh], [k.x + bw, Q2Y - bh], [k.x + bw, Q2Y + bh], [k.x - bw, Q2Y + bh], [k.x - bw, Q2Y - bh]], { w: 6, color: RED, seed: 560 + ans, passes: 1, step: 20, prog: P(t, nt, nt + 0.5) });
          wrapLines(note, 300, 30).forEach((l, j) => hand(l, k.x, Q2Y + Q2H / 2 + 50 + j * 34, 30, nt + 0.2 + j * 0.3, { color: C.cream, seed: 562 + j, dur: 0.4 }));
        }
      }
      // after: no math in superposition; not every law is an equation; older rocks are underneath
      if (t >= tm.noMath) tag('no math in it!', Q2[0].x, Q2Y - Q2H / 2 - 34, 30, pop(t, tm.noMath, 0.35), { seed: 570, rot: -0.05, color: STICKY });
      if (t >= tm.eq) sticky(['EVERY LAW IS', 'AN EQUATION'], 1300, 830, tm.eq - 0.2, { w: 270, size: 32, color: PINK, seed: 572, strike: tm.eq + 0.3, rot: 0.04 });
      const rk = pop(t, tm.older, 0.4, 1.6);
      if (rk > 0) {
        prop('rock_layers', 700, 925, 150, { s: rk });
        hand('older rocks underneath', 900, 850, 30, tm.older + 0.3, { align: 'left', color: C.cream, seed: 574, dur: 0.5 });
        inkArrow(890, 870, 790, 905, { w: 4, color: C.cream, seed: 575, prog: P(t, tm.older + 0.5, tm.older + 0.9), bulge: 10, head: 16 });
      }
      if (docked()) {
        const L = Q2.map((k, i) => { const it = item(k.id), u = k.kind === 'law' ? flips.what : flips.why; return u >= 0.5 ? `${it.short}: ${k.kind === 'law' ? 'LAW (what)' : 'THEORY (why)'}: ${it.kid}.` : `${i + 1}. ${it.name}`; });
        if (t >= tm.noMath) L.push('Superposition has no math in it.');
        if (t >= tm.eq) L.push('Not every law is an equation.');
        Dock.text('s2-three', L, { cls: 'note' });
      }
    }
    cast(t, ctx);
  };
  S.s2 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.4, after: 2.2 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part), rs = replyStart(c, q);
      const w = c.W('s2_01', 'wrench');
      out.push({ name: 'whoosh', t: w, gain: 0.3 }, { name: 'thunk', t: w + 0.5, gain: 0.45 }, { name: fx('card_flip', 'paper'), t: c.W('s2_01', 'gravitation'), gain: 0.3 }, { name: 'stamp', t: c.W('s2_01', 'math'), gain: 0.3 });
      out.push({ name: 'paper', t: c.W('s2_02', 'wrote'), gain: 0.35 }, { name: 'scribble', t: c.W('s2_02', 'frame'), gain: 0.2 });
      out.push({ name: fx('card_flip', 'paper'), t: c.W('s2_03', 'relativity'), gain: 0.3 }, { name: 'stamp', t: c.W('s2_03', 'bent'), gain: 0.3 });
      [0, 1, 2].forEach(i => out.push({ name: 'pop', t: c.W('s2_04', 'Quiz') + 0.1 + i * 0.15, gain: 0.2 }));
      out.push({ name: fx('card_flip', 'paper'), t: branch(c, { q2_right: 'Moving', q2_wrong0: 'why', q2_wrong2: 'Plate' }), gain: 0.35 });
      out.push({ name: fx('card_flip', 'paper'), t: branch(c, { q2_right: 'describe', q2_wrong0: 'describes', q2_wrong2: 'describes' }), gain: 0.35 });
      out.push({ name: fx('chalk_squeak', 'scribble'), t: part.kind === 'reply' && ansOf(ctx, part) !== q.correct ? rs + 0.3 : -1e6, gain: 0.3 });
      out.push({ name: 'paper', t: c.W('s2_05', 'math'), gain: 0.2 }, { name: fx('chalk_squeak', 'scribble'), t: c.W('s2_06', 'equation') + 0.3, gain: 0.3 }, { name: 'thunk', t: c.W('s2_06', 'older'), gain: 0.25 });
      return out;
    },
  };

  /* ---- s3 "A pattern in the sky": the telescope, NASA's planets, T vs a, T² vs a³ on a line, q3 Neptune ---- */
  const S3 = { graph: { x: 990, y: 560, w: 840, h: 600 }, tele: { x: 420, y: 500, h: 250 }, zoom: { x: 830, y: 455 }, kepler: { x: 830, y: 470 }, ratio: { x: 1230, y: 700 },
    rgraph: { x: 1240, y: 560, w: 470, h: 560 }, wk: { x: 440, y: 232, w: 560 } };
  const S3A = { graph: { x: 1180, y: 560, w: 580, h: 560 }, col: 640 };
  BODY.s3 = function (t, part, ctx, c) {
    const N = nums(), C = FC(), q = part.seg.question, ans = ansOf(ctx, part), rs = replyStart(c, q);
    bg(930);
    const tm = {
      tele: c.L('s3_01') - 0.4, g1: c.W('s3_01', 'NASA'), pts1: c.W('s3_01', 'distance'), curve: c.W('s3_01', 'year') + 0.8,
      swap: c.W('s3_02', 'Plot'), pts2: c.W('s3_02', 'cubed'), line: c.W('s3_02', 'straight'), ratio: c.W('s3_02', 'equals'),
      kepler: c.W('s3_03', 'law'), desc: c.W('s3_03', 'describes'), nep: c.W('s3_04', 'Neptune'),
      guessed: c.W('s3_05', 'guessed'), predicted: c.W('s3_06', 'Predicted'), explained: c.W('s3_06', 'explained'), nasa: c.W('s3_06', 'NASA'), labels: c.W('s3_06', 'labels'),
    };
    const reply = part.kind === 'reply', after = part.kind === 'after';
    if (part.kind === 'lines') prop('telescope', S3.tele.x, S3.tele.y, S3.tele.h, { s: pop(t, tm.tele, 0.4, 1.6) });
    if (part.kind === 'lines') {
      const G_ = S3.graph;
      if (t < tm.swap) {
        const s = pop(t, tm.g1, 0.4, 1.6);
        if (s > 0) { g.save(); g.translate(G_.x, G_.y); g.scale(s, s); g.translate(-G_.x, -G_.y); plotTa(G_.x, G_.y, G_.w, G_.h, tm.pts1); g.restore(); }
        if (t >= tm.curve) hand('a curve', G_.x + 60, G_.y + 40, 40, tm.curve, { color: RED, seed: 600, dur: 0.4, rot: -0.1 });
      } else {
        const s = E.outBack(P(t, tm.swap, tm.swap + 0.4), 1.4);
        g.save(); g.translate(G_.x, G_.y); g.scale(1, Math.max(0.02, s)); g.translate(-G_.x, -G_.y);
        plotT2a3(G_.x, G_.y, G_.w, G_.h, tm.pts2, E.inOutSine(P(t, tm.line, tm.line + 1.2)));
        g.restore();
        if (t < tm.kepler) innerZoom(S3.zoom.x, S3.zoom.y, pop(t, tm.line + 0.8, 0.35, 1.6), tm.line + 0.9);
        if (t >= tm.ratio) {
          tag('T² ÷ a³ = about 1', S3.ratio.x, S3.ratio.y, 40, pop(t, tm.ratio, 0.35), { font: HANDF, seed: 602, rot: -0.03, color: STICKY });
          hand(`all eight: ${N.rminText} to ${N.rmaxText}`, S3.ratio.x, S3.ratio.y + 50, 26, tm.ratio + 0.4, { color: INK, seed: 603, dur: 0.4 });
        }
        hand('NASA fact sheets, sidereal periods', G_.x + G_.w / 2 - 20, G_.y + G_.h / 2 + 34, 26, tm.swap + 0.5, { align: 'right', color: C.cream, seed: 604, dur: 0.5 });
        wcard('law', S3.kepler.x, S3.kepler.y, 340, 170, { title: `KEPLER'S THIRD LAW (${YEARS.kepler})`, body: 'describes the pattern', stamp: t >= tm.desc, seed: 610, rot: -0.02, t0: tm.kepler, bodyT: tm.desc, size: 34 });
        if (t >= tm.nep) tag(`Neptune: ${N.q3.a} AU`, G_.x + 190, G_.y - 150, 30, pop(t, tm.nep, 0.35), { seed: 612, rot: 0.03, color: C.cream });
      }
      if (docked()) {
        const L = [];
        if (t >= tm.g1) L.push("NASA's data: each planet's distance a (AU) and year T (years).");
        if (t >= tm.curve && t < tm.swap) L.push('T against a: a curve.');
        if (t >= tm.line) L.push('T² against a³: all eight on one straight line.');
        if (t >= tm.ratio) L.push(`T² ÷ a³ = about 1 (all eight: ${N.rminText} to ${N.rmaxText}).`);
        if (t >= tm.kepler) L.push(`KEPLER'S THIRD LAW (${YEARS.kepler}): describes the pattern.`);
        if (L.length) Dock.text('s3-data', L, { cls: 'note' });
      }
    } else if (reply) {
      // the working (every branch), then Neptune's predicted dot lands beside NASA's
      const Wt = stepTimes(c, q);
      const Gw = workCard(q, S3.wk.x, S3.wk.y, S3.wk.w, Wt);
      const R = S3.rgraph, gs = pop(t, rs + 0.1, 0.4, 1.6);
      if (gs > 0) {
        g.save(); g.translate(R.x, R.y); g.scale(gs, gs); g.translate(-R.x, -R.y);
        const G = plotTa(R.x, R.y, R.w, R.h, -1e6, { title: 'year T against distance a', noNep: true });
        g.restore();
        const dotT = Wt.steps[1] + 0.9, nasaT = Wt.steps[2] + 0.3;
        if (gs >= 1 && t >= dotT - 0.5) {
          const p = G.at(N.q3.a, N.q3.T), u = E.outBounce ? E.outBounce(P(t, dotT - 0.5, dotT)) : E.outCubic(P(t, dotT - 0.5, dotT));
          const py = lerp(p.y - 220, p.y, u);
          inkCircle(p.x, py, 18, { w: 5, color: RED, seed: 620, passes: 1 });
          if (t >= dotT) tag(`Neptune, predicted: ${N.predicted}`, p.x - 180, p.y + 4, 28, pop(t, dotT, 0.3), { seed: 621, rot: -0.03, color: STICKY });
          if (t >= nasaT) tag(`NASA: ${N.nasa}`, p.x - 120, p.y + 62, 28, pop(t, nasaT, 0.3), { seed: 622, rot: 0.03, color: C.law, ink: C.cream });
          Vid.lastDot = { landed: t >= dotT, nasa: t >= nasaT, predicted: N.predicted, nasaYears: N.nasa };
        }
      }
      if (ans !== q.correct && ans != null) {
        const gy = S3.wk.y + (Gw ? Gw.h : 480) + 70;
        sticky([`${q.options[ans]}?`], S3.wk.x + 200, Math.min(gy, 860), rs + 0.1, { w: 300, size: 36, color: PINK, seed: 630 + ans, strike: branch(c, { q3_wrong0: 'skips', q3_wrong1: 'squared' }) + 0.2, rot: 0.04 });
      }
      if (docked() && Vid.lastDot && Vid.lastDot.landed) Dock.text('s3-dot', [`Neptune: predicted ${N.predicted} years; NASA measures ${N.nasa} years.`], { cls: 'note' });
    } else {
      // after: the plot on the right; left: GUESSED struck (PREDICTED), Kepler (WHAT) + gravitation (WHY), NASA's label note
      const G_ = S3A.graph;
      plotT2a3(G_.x, G_.y, G_.w, G_.h, -1e6, 1);
      const X = S3A.col;
      sticky(['GUESSED'], X, 300, tm.guessed, { w: 240, size: 40, seed: 640, strike: tm.predicted + 0.1, rot: -0.05 });
      if (t >= tm.predicted) hand('PREDICTED', X + 20, 372, 44, tm.predicted + 0.3, { color: RED, seed: 641, dur: 0.4, rot: -0.04 });
      wcard('law', X, 520, 360, 170, { title: `KEPLER'S THIRD LAW (${YEARS.kepler})`, body: 'describes the pattern', stamp: true, seed: 610, rot: -0.02, size: 34 });
      wcard('theory', X, 700, 360, 170, { title: "NEWTON'S GRAVITATION", body: 'explains why the pattern works', stamp: true, seed: 642, rot: 0.02, t0: tm.explained, bodyT: tm.explained + 0.2, size: 32 });
      if (t >= tm.nasa) {
        const ln = wrapLines("NASA calls Newton's gravitation a theory here: the names are historical", 520, 26);
        const h = 24 + ln.length * 30, s = pop(t, tm.nasa, 0.35);
        g.save(); g.translate(X + 40, 860); g.rotate(0.01); g.scale(s, s); paperRect(0, 0, 560, h, C.cream, { seed: 644, depth: 1, boil: false }); g.restore();
        if (s >= 1) ln.forEach((l, j) => hand(l, X + 40, 860 - h / 2 + 27 + j * 30, 26, tm.nasa + 0.2 + j * 0.3, { color: INK, seed: 645 + j, dur: 0.4 }));
      }
      if (t >= tm.labels) stampMark('NOT RANKS', G_.x + 130, G_.y + 150, stampS(t, tm.labels) * 0.7, { rot: -0.14, color: RED, size: 60 });
      if (docked()) {
        const L = [];
        if (t >= tm.predicted) L.push('Not guessed: PREDICTED.');
        L.push(`KEPLER'S THIRD LAW (${YEARS.kepler}): describes the pattern (WHAT).`);
        if (t >= tm.explained) L.push("NEWTON'S GRAVITATION: explains why the pattern works (WHY).");
        if (t >= tm.nasa) L.push("NASA calls Newton's gravitation a theory: the names are historical, not ranks.");
        Dock.text('s3-after', L, { cls: 'note' });
      }
    }
    cast(t, ctx);
  };
  S.s3 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.8, after: 2.6 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part), W = stepTimes(c, q);
      out.push({ name: 'thunk', t: c.L('s3_01') - 0.4, gain: 0.3 }, { name: 'paper', t: c.W('s3_01', 'NASA'), gain: 0.35 });
      for (let i = 0; i < 8; i++) out.push({ name: 'pop', t: c.W('s3_01', 'distance') + i * 0.12, gain: 0.12 });
      out.push({ name: fx('card_flip', 'paper'), t: c.W('s3_02', 'Plot'), gain: 0.35 });
      for (let i = 0; i < 8; i++) out.push({ name: 'pop', t: c.W('s3_02', 'cubed') + i * 0.12, gain: 0.12 });
      out.push({ name: 'scribble', t: c.W('s3_02', 'straight'), gain: 0.25 }, { name: 'ding', t: c.W('s3_02', 'equals'), gain: 0.25 });
      out.push({ name: fx('card_flip', 'paper'), t: c.W('s3_03', 'law'), gain: 0.3 }, { name: 'stamp', t: c.W('s3_03', 'describes'), gain: 0.3 }, { name: 'paper', t: c.W('s3_04', 'Neptune'), gain: 0.25 });
      out.push(...stepSfx(W));
      if (W) out.push({ name: 'boing', t: W.steps[1] + 0.9, gain: 0.3 }, { name: 'paper', t: W.steps[2] + 0.3, gain: 0.25 });
      out.push({ name: fx('chalk_squeak', 'scribble'), t: branch(c, { q3_wrong0: 'skips', q3_wrong1: 'squared' }) + 0.2, gain: 0.3 });
      out.push({ name: 'paper', t: c.W('s3_05', 'guessed'), gain: 0.25 }, { name: fx('chalk_squeak', 'scribble'), t: c.W('s3_06', 'Predicted') + 0.1, gain: 0.3 });
      out.push({ name: fx('card_flip', 'paper'), t: c.W('s3_06', 'explained'), gain: 0.3 }, { name: 'paper', t: c.W('s3_06', 'NASA'), gain: 0.25 }, { name: 'stamp', t: c.W('s3_06', 'labels'), gain: 0.4 });
      return out;
    },
  };

  /* ---- s4 "Proven forever?": PROVEN struck → TESTED, WITH LIMITS; the rocket, the atom; the drifting continents; MODIFIED ---- */
  const S4 = { board: { x: 800, y: 520, w: 720, h: 520 }, col: 1320, map: { x: 930, y: 470, w: 820, h: 440 } };
  function continents(t, x, y, s, t0) {
    if (s <= 0.001) return;
    const M = S4.map, C = FC(), d = E.inOutSine(P(t, t0, t0 + 3));
    g.save(); g.translate(x, y); g.scale(s, s);
    layer('ocean', -M.w / 2 - 10, -M.h / 2 - 10, M.w + 20, M.h + 20, () => {
      paperRect(0, 0, M.w, M.h, '#b9d3e6', { seed: 650, depth: 1.3, boil: false });
      for (let r = 0; r < 9; r++) { const yy = -M.h / 2 + 30 + r * 46, pts = []; for (let k = 0; k <= 20; k++) pts.push([-M.w / 2 + 20 + k * (M.w - 40) / 20, yy + Math.sin(k * 1.3 + r) * 4]); ink(pts, { w: 1.5, color: 'rgba(37,60,90,0.3)', seed: 651 + r, passes: 1, step: 20, amp: 0.3 }); }
      g.strokeStyle = 'rgba(37,60,90,0.5)'; g.lineWidth = 3; g.strokeRect(-M.w / 2 + 12, -M.h / 2 + 12, M.w - 24, M.h - 24);
    }, { zoom: 0.8 });
    const sep = 20 + d * 170;
    paperPiece(SH.sa, -60 - sep, 10, '#c9b48a', { seed: 660, depth: 1 });
    paperPiece(SH.af, 70 + sep, 0, '#c9b48a', { seed: 661, depth: 1 });
    hatch(-110 - sep, -60, -80 - sep, 60, 4, 662, 'rgba(90,60,30,0.35)', 2);
    hatch(40 + sep, -60, 90 + sep, 60, 4, 666, 'rgba(90,60,30,0.35)', 2);
    hand('South America', -60 - sep, 170, 26, -1e6, { dur: 0.01, color: C.navy, seed: 668 });
    hand('Africa', 70 + sep, 160, 26, -1e6, { dur: 0.01, color: C.navy, seed: 669 });
    if (d > 0.05) for (let k = 0; k < 3; k++) { const yy = -80 + k * 80; inkArrow(-60 - sep + 70, yy, -60 - sep + 20, yy, { w: 3, color: C.navy, seed: 670 + k, head: 12, prog: d }); inkArrow(70 + sep - 70, yy, 70 + sep - 20, yy, { w: 3, color: C.navy, seed: 674 + k, head: 12, prog: d }); }
    g.restore();
  }
  BODY.s4 = function (t, part, ctx, c) {
    const C = FC(), q = part.seg.question, ans = ansOf(ctx, part), rs = replyStart(c, q);
    bg(930);
    const tm = {
      board: c.L('s4_01') - 0.3, sticky: c.W('s4_01', 'proven'), strike: c.W('s4_02', 'Tested'), tested: c.W('s4_02', 'Tested') + 0.5,
      approx: c.W('s4_02', 'approximation'), rocket: c.W('s4_02', 'spacecraft'), limits: c.W('s4_02', 'limits'),
      theories: c.W('s4_03', 'Theories'), y1912: c.W('s4_03', 'twelve'), drift: c.W('s4_03', 'drift'), sea: c.W('s4_03', 'seafloor'), y1960: c.W('s4_03', 'sixties'),
      patch: branch(c, { q4_right: 'Modified', q4_wrong0: 'modified', q4_wrong2: 'modified' }),
      wrongStrike: branch(c, { q4_wrong0: 'Not', q4_wrong2: 'Ignoring' }),
      mind: c.W('s4_04', 'mind'), check: c.W('s4_05', 'check'), whim: c.W('s4_05', 'whim'), good: c.W('s4_05', 'good'),
    };
    const lines = part.kind === 'lines';
    // the poster (s4_01–02), off the stage when the drift story starts (s4_03)
    const po = lines ? gone(t, tm.theories - 0.1, 0.5) : 0;
    if (po > 0) {
      const B = S4.board, dy = (1 - po) * 700;
      g.save(); g.translate(0, dy);
      F().table(B.x, B.y + B.h / 2 + 22, 860, { seed: 23, s: pop(t, tm.board - 0.2, 0.4, 1.6) });
      const bs = pop(t, tm.board, 0.45, 1.6);
      F().board(B.x, B.y, B.w, B.h, { title: "MAX'S NOTES", seed: 53, s: bs, art: true });
      if (bs >= 1) {
        sticky(['LAWS =', 'PROVEN', 'FOREVER'], B.x, B.y + 20, tm.sticky, { w: 300, size: 44, seed: 680, rot: -0.03, strikeLines: [{ i: 1, t: tm.strike }, { i: 2, t: tm.strike + 0.25 }] });
        if (t >= tm.tested) { hand('TESTED,', B.x + 205, B.y - 30, 44, tm.tested, { color: RED, seed: 683, dur: 0.35, rot: -0.05, font: MARK }); hand('WITH LIMITS', B.x + 205, B.y + 26, 40, tm.limits, { color: RED, seed: 684, dur: 0.4, rot: -0.05, font: MARK }); }
      }
      g.restore();
      // the right column: NASA's rocket, the atom (limits)
      const X = S4.col;
      const rs_ = pop(t, tm.rocket, 0.4, 1.8) * po;
      if (rs_ > 0) {
        wcard('plain', X + 20, 450, 330, 150, { body: "NASA still uses Newton's laws for spacecraft paths", stamp: false, seed: 690, rot: 0.02, s: rs_, size: 28 });
        rocket(X + 150, 330, rs_);
      }
      const as = pop(t, tm.limits, 0.4, 1.8) * po;
      if (as > 0) {
        wcard('plain', X + 20, 720, 330, 150, { body: 'conservation of mass: not in nuclear reactions', stamp: false, seed: 692, rot: -0.02, s: as, size: 28 });
        atom(X + 150, 630, as);
      }
      if (docked()) {
        const L = [];
        if (t >= tm.sticky) L.push('Sticky: LAWS = PROVEN FOREVER' + (t >= tm.strike ? ' (PROVEN FOREVER crossed out)' : ''));
        if (t >= tm.tested) L.push('TESTED, WITH LIMITS');
        if (t >= tm.rocket) L.push("NASA still uses Newton's laws for spacecraft paths.");
        if (t >= tm.limits) L.push('Conservation of mass: not in nuclear reactions.');
        if (L.length) Dock.text('s4-poster', L, { cls: 'note' });
      }
    }
    // the drifting continents (s4_03) and the two year cards; smaller (left) in the reply and after
    const M = S4.map, big = lines, ms = big ? pop(t, tm.theories + 0.2, 0.45, 1.6) : 0.55;
    const mx = big ? M.x : 680, my = big ? M.y : 380;
    continents(t, mx, my, ms, tm.drift);
    if (big) {
      const y1 = pop(t, tm.y1912, 0.35), y2 = pop(t, tm.y1960, 0.35);
      if (y1 > 0) wcard('plain', 690, 810, 380, 120, { title: String(YEARS.drift), body: 'continental drift', stamp: false, seed: 700, rot: -0.03, s: y1, size: 34 });
      if (y2 > 0) wcard('plain', 1150, 810, 480, 140, { title: YEARS.seafloor, body: 'new evidence from the seafloor: plate tectonics', stamp: false, seed: 702, rot: 0.02, s: y2, size: 30 });
      if (docked() && t >= tm.y1912) Dock.text('s4-drift', [`${YEARS.drift}: continental drift`].concat(t >= tm.y1960 ? [`${YEARS.seafloor}: new evidence from the seafloor, plate tectonics`] : []), { cls: 'note' });
    } else {
      tag(`${YEARS.drift} drift, ${YEARS.seafloor} seafloor evidence`, 680, 530, 26, 1, { seed: 704, rot: -0.02, color: C.cream });
      // the theory card; the MODIFIED patch is sewn on (every branch)
      const it = item('plateTectonics'), K = { x: 1110, y: 520, w: 520, h: 300 };
      wcard('theory', K.x, K.y, K.w, K.h, { title: 'THEORY: ' + it.short.toUpperCase(), body: it.kid, stamp: true, seed: 710, rot: 0.015, size: 36, s: part.kind === 'reply' ? pop(t, rs + 0.1, 0.4, 1.6) : 1 });
      const ps = stampS(t, tm.patch);
      if (ps > 0) {
        g.save(); g.translate(K.x - 70, K.y + 108); g.rotate(-0.06); g.scale(ps, ps);
        paperPiece(SH.patch, 0, 0, '#d9c28f', { seed: 712, depth: 1 });
        for (let k = 0; k < 24; k++) { const u = k / 24, px = -110 + u * 220; ink([[px, -36], [px + 5, -32]], { w: 2.5, color: RED, seed: 713 + k, passes: 1, step: 4, prog: P(t, tm.patch + 0.1 + u * 0.4, tm.patch + 0.15 + u * 0.4) }); ink([[px, 36], [px + 5, 32]], { w: 2.5, color: RED, seed: 740 + k, passes: 1, step: 4, prog: P(t, tm.patch + 0.1 + u * 0.4, tm.patch + 0.15 + u * 0.4) }); }
        txt('MODIFIED', 0, 2, 46, { font: SLAB, color: '#6b2a14' });
        g.restore();
      }
      if (part.kind === 'reply' && ans !== q.correct && ans != null) {
        sticky(ans === 0 ? ['THROWN OUT', 'AT ONCE?'] : ['IGNORE THE', 'EVIDENCE?'], 680, 760, rs + 0.1, { w: 280, size: 36, color: PINK, seed: 720 + ans, strike: tm.wrongStrike + 0.2, rot: 0.04 });
      }
      if (part.kind === 'after') {
        // other scientists check it: five magnifiers, each ticked; NOT on a whim
        for (let k = 0; k < 5; k++) { const t0 = tm.check + k * 0.15; magnifier(900 + k * 100, 780, pop(t, t0, 0.3, 2), P(t, t0 + 0.2, t0 + 0.5), 750 + k * 4); }
        if (t >= tm.check) hand('other scientists check it', 1100, 850, 32, tm.check + 0.3, { color: C.cream, seed: 772, dur: 0.5 });
        sticky(['ON A WHIM'], 680, 770, tm.whim - 0.1, { w: 270, size: 40, color: PINK, seed: 774, strike: tm.whim + 0.3, rot: -0.04 });
        if (t >= tm.good) tag('the good part', 1110, 330, 34, pop(t, tm.good, 0.35), { seed: 776, rot: -0.04, color: STICKY });
      }
      if (docked()) {
        const L = [`THEORY: ${it.short}: ${it.kid}.`];
        if (t >= tm.patch) L.push('Patch sewn on: MODIFIED (rarely thrown out).');
        if (part.kind === 'after' && t >= tm.check) L.push('Other scientists check the evidence. Not on a whim.');
        Dock.text('s4-theory', L, { cls: 'note' });
      }
    }
    cast(t, ctx);
  };
  S.s4 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.6, reply: 2.4, after: 2.4 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: fx('poster_thump', 'thunk'), t: c.L('s4_01') - 0.3, gain: 0.3 }, { name: 'paper', t: c.W('s4_01', 'proven'), gain: 0.3 });
      out.push({ name: fx('chalk_squeak', 'scribble'), t: c.W('s4_02', 'Tested'), gain: 0.35 }, { name: 'scribble', t: c.W('s4_02', 'Tested') + 0.5, gain: 0.25 });
      out.push({ name: 'whoosh', t: c.W('s4_02', 'spacecraft'), gain: 0.3 }, { name: 'pop', t: c.W('s4_02', 'limits'), gain: 0.25 });
      out.push({ name: 'whoosh', t: c.W('s4_03', 'Theories') - 0.1, gain: 0.3 }, { name: 'paper', t: c.W('s4_03', 'twelve'), gain: 0.25 }, { name: 'paper', t: c.W('s4_03', 'sixties'), gain: 0.25 });
      out.push({ name: 'stamp', t: branch(c, { q4_right: 'Modified', q4_wrong0: 'modified', q4_wrong2: 'modified' }), gain: 0.4 });
      out.push({ name: fx('chalk_squeak', 'scribble'), t: branch(c, { q4_wrong0: 'Not', q4_wrong2: 'Ignoring' }) + 0.2, gain: 0.3 });
      void q;
      for (let k = 0; k < 5; k++) out.push({ name: 'pop', t: c.W('s4_05', 'check') + k * 0.15, gain: 0.15 });
      out.push({ name: fx('chalk_squeak', 'scribble'), t: c.W('s4_05', 'whim') + 0.3, gain: 0.3 }, { name: 'ding', t: c.W('s4_05', 'good'), gain: 0.25 });
      return out;
    },
  };

  /* ---- s5 "Different jobs": three cards on the poster, no ladder; the watered pot; DIFFERENT JOBS; TESTED ---- */
  const B5 = { x: 900, y: 510, w: 1040, h: 560 };
  const CARDS5 = [
    { term: 'hypothesis', kind: 'hypothesis', dx: -350, cue: ['s5_01', 'hypothesis'] },
    { term: 'theory', kind: 'theory', dx: 0, cue: ['s5_01', 'theory'] },
    { term: 'law', kind: 'law', dx: 350, cue: ['s5_02', 'law'] },
  ];
  BODY.s5 = function (t, part, ctx, c) {
    const C = FC();
    bg(930);
    const tm = {
      board: c.L('s5_01') - 0.4, hunch: c.W('s5_01', 'hunch'), jobs: c.W('s5_02', 'Different'), guess: c.W('s5_02', 'guess'),
      title: c.W('s5_03', 'title'), newTitle: c.W('s5_03', 'Different'), shelf: c.W('s5_03', 'shelf'), stamp: c.W('s5_04', 'Tested'),
    };
    const potT = c.L('s5_02');
    F().table(B5.x, B5.y + B5.h / 2 + 30, 1160, { seed: 25, s: pop(t, tm.board - 0.2, 0.4, 1.6) });
    const bs = pop(t, tm.board, 0.45, 1.6);
    F().board(B5.x, B5.y, B5.w, B5.h, { title: 'THE SCIENCE LADDER', seed: 55, s: bs, art: true });
    if (bs >= 1) {
      // the new title: the old one struck, a fresh strip over it (s5_03)
      const ty = B5.y - B5.h / 2 + 58;
      if (t >= tm.title) strike(B5.x - 200, ty + 6, B5.x + 200, ty - 6, P(t, tm.title, tm.title + 0.35), 790);
      const ns = pop(t, tm.newTitle, 0.35, 2);
      if (ns > 0) {
        g.save(); g.translate(B5.x, ty + 4); g.rotate(-0.02); g.scale(ns, ns);
        paperRect(0, 0, 440, 78, C.cream, { seed: 791, depth: 1, boil: false });
        g.restore();
        hand('DIFFERENT JOBS', B5.x, ty + 6, 56, tm.newTitle + 0.15, { color: RED, seed: 792, dur: 0.5, font: MARK, rot: -0.02 });
      }
      // the shelf (s5_03): a plank under the three cards
      const sh = pop(t, tm.shelf, 0.35, 1.6);
      if (sh > 0) { g.save(); g.translate(B5.x, 712); g.scale(sh, 1); paperRect(0, 0, 1000, 20, C.table, { seed: 793, depth: 1, boil: false, tex: 'tex_kraft' }); g.restore(); }
      CARDS5.forEach((k, i) => {
        const tt = term(k.term), t0 = c.W(k.cue[0], k.cue[1]);
        wcard(k.kind, B5.x + k.dx, 550, 310, 300, { title: tt.word.toUpperCase(), body: tt.kid, stamp: true, seed: 800 + i * 7, rot: (i - 1) * 0.02, t0, bodyT: t0 + 0.2, size: 34 });
      });
      if (t >= tm.hunch) tag("not one person's hunch", B5.x, 380, 30, pop(t, tm.hunch, 0.35), { seed: 820, rot: 0.03, color: STICKY });
      if (t >= tm.guess) tag('neither one is a guess', B5.x + 350, 380, 28, pop(t, tm.guess, 0.35), { seed: 821, rot: -0.03, color: STICKY });
    }
    // the watered pot: THEORY, a watering can; nothing sprouts
    const ps = pop(t, potT, 0.35);
    if (ps > 0) {
      prop('flower_pot', 1425, 812, 140, { s: ps });
      tag('THEORY', 1425, 754, 24, ps, { font: SLAB, seed: 830, color: C.amber, rot: 0.03 });
      wateringCan(1530, 610, t, potT + 0.4, ps);
      if (t >= tm.jobs + 0.8) hand('still no law', 1415, 870, 30, tm.jobs + 0.8, { color: RED, seed: 831, dur: 0.4 });
    }
    stampMark('TESTED', B5.x + 120, 755, stampS(t, tm.stamp) * 1.1, { rot: -0.14, size: 80, color: RED });
    if (docked()) {
      const L = [];
      CARDS5.forEach(k => { if (t >= c.W(k.cue[0], k.cue[1])) L.push(`${term(k.term).word.toUpperCase()}: ${term(k.term).kid}.`); });
      if (t >= tm.newTitle) L.push('New title: DIFFERENT JOBS. The ladder is now a shelf.');
      if (t >= tm.stamp) L.push('TESTED');
      if (L.length) Dock.text('s5-cards', L, { cls: 'note' });
    }
    cast(t, ctx);
    Vid.lastStamp = t >= tm.stamp + 0.25;
  };
  S.s5 = {
    timing: { lead: { lines: CARD.lead }, hold: { lines: 3.0 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: fx('poster_thump', 'thunk'), t: c.L('s5_01') - 0.4, gain: 0.3 });
      CARDS5.forEach(k => out.push({ name: fx('card_flip', 'paper'), t: c.W(k.cue[0], k.cue[1]), gain: 0.3 }));
      out.push({ name: 'pop', t: c.L('s5_02'), gain: 0.2 }, { name: 'sparkle', t: c.L('s5_02') + 0.8, gain: 0.2 });
      out.push({ name: fx('chalk_squeak', 'scribble'), t: c.W('s5_03', 'title'), gain: 0.3 }, { name: 'scribble', t: c.W('s5_03', 'Different'), gain: 0.25 }, { name: 'thunk', t: c.W('s5_03', 'shelf'), gain: 0.3 });
      out.push({ name: 'stamp', t: c.W('s5_04', 'Tested'), gain: 0.6 }, { name: 'sparkle', t: c.W('s5_04', 'Tested') + 0.3, gain: 0.35 });
      return out;
    },
  };

  /* a segment without its own scene: its storyboard note on a card */
  const STAGE = {
    draw(t, part, ctx) {
      bg(930);
      const lines = wrapLines(part.seg.visual || String(part.seg.chapter || part.seg.id), 760, 34).slice(0, 7);
      paperRect(960, 480, 860, 80 + lines.length * 44, FC().cream, { seed: 12, depth: 1.2 });
      lines.forEach((l, i) => txt(l, 960, 480 - lines.length * 22 + 22 + i * 44, 34, { color: NAVY }));
      cast(t, ctx);
    },
  };

  /* wrap every scene: the chapter card that tears away to the scene; sfx outside the part are dropped */
  for (const id of Object.keys(BODY)) {
    S[id].draw = function (t, part, ctx) {
      const c = cues(ctx), i = segIndex(part.seg);
      if (part.kind === 'lines' && t < CARD.hold + CARD.tear) {
        if (t < CARD.hold) drawCard(i);
        else tornTransition(() => drawCard(i), u => BODY[id](u, part, ctx, c), t, CARD.hold, CARD.tear, 40 + i);
      } else BODY[id](t, part, ctx, c);
    };
    const sfx = S[id].sfx;
    S[id].sfx = (part, ctx) => sfx(part, ctx).filter(e => e && e.name && e.t > -1e5 && e.t < 1e5 && e.gain > 0);
  }

  class VideoScene extends Player.Scene {
    enter() {
      NUMS = null; WK.clear(); WLAY.clear();          // recomputed from the loaded content
      for (const sg of (Game.content.script || { segments: [] }).segments) if (!S[sg.id]) S[sg.id] = STAGE;
      super.enter();
      if (typeof prewarm === 'function' && prewarm.draw) prewarm.draw(() => bg(930));
    }
  }
  Levels.video = VideoScene;
})();
